import {
  Button,
  Card,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spacer,
} from '@nextui-org/react';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useImperativeHandle,
} from 'react';
import { FaPlusCircle, FaTrash } from 'react-icons/fa';

import { IParceiro } from '../../interfaces/response/Parceiros/IParceiro';
import { TipoParceiro } from '../../pages/Parceiros/hooks/useParceiros';
import useRegisterParceiroModal from './hooks/useRegisterParceiroModal';

const normalizeCep = (cep: string): string => (cep ?? '').replace(/\D/g, '');
const cepMask = (value: string): string => {
  const digits = normalizeCep(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

export interface RegisterParceiroHandle {
  openToEdit: (parceiroAux: IParceiro) => void;
  closeModal: () => void;
}

interface RegisterParceiroProps {
  onUpdate?: () => void;
  useModalStyle: boolean;
  tipo: TipoParceiro;
}

const RegisterParceiro: ForwardRefRenderFunction<
  RegisterParceiroHandle,
  RegisterParceiroProps
> = ({ onUpdate, useModalStyle, tipo }, ref): JSX.Element => {
  const {
    onOpen,
    isOpen,
    onOpenChange,
    theme,
    handleClose,
    handleSubmit,
    handleOpenEdit,
    handleSubmitParceiro,
    handleDeleteParceiro,
    errors,
    register,
    isLoading,
    isDeleting,
    isValid,
    setValue,
    values,
    parceiro,
  } = useRegisterParceiroModal({ onUpdate, tipo });

  useImperativeHandle(ref, () => ({
    openToEdit: (parceiroAux: IParceiro) => {
      handleOpenEdit(parceiroAux);
    },
    closeModal: () => {
      handleClose();
    },
  }));

  // seguindo o padrão do RegisterCubage, mas aqui a tela sempre usa modal
  if (!useModalStyle) {
    return (
      <Button
        onPress={onOpen}
        size="md"
        startContent={
          <div>
            <FaPlusCircle />
          </div>
        }
      >
        <p>Cadastrar parceiro</p>
      </Button>
    );
  }

  return (
    <>
      <Button
        onPress={onOpen}
        size="md"
        startContent={
          <div>
            <FaPlusCircle />
          </div>
        }
      >
        <p>Cadastrar parceiro</p>
      </Button>

      <Modal
        isOpen={isOpen}
        size="4xl"
        onOpenChange={onOpenChange}
        className={`${theme}`}
        onClose={handleClose}
      >
        <ModalContent>
          <form onSubmit={handleSubmit(handleSubmitParceiro)}>
            <ModalHeader className="text-default-600">
              <p>{parceiro ? 'Editar' : 'Cadastrar'} parceiro</p>
            </ModalHeader>

            <ModalBody>
              {parceiro && (
                <Card className="flex-row  bg-foreground-100 p-4" shadow="none">
                  <p className="font-semibold">ID:</p>
                  <span className="break-all px-2 text-default-600 ">
                    {parceiro.id}
                  </span>
                </Card>
              )}

              <Spacer y={1} />

              <div className="grid grid-cols-3 gap-4">
                <Input
                  size="md"
                  type="text"
                  label="Sigla"
                  variant="underlined"
                  classNames={{ label: 'font-bold' }}
                  errorMessage={errors.sigla?.message}
                  {...register('sigla')}
                  value={values.sigla}
                  onValueChange={(value) => {
                    setValue('sigla', value);
                  }}
                />

                <Input
                  size="md"
                  type="text"
                  label="CEP Início"
                  variant="underlined"
                  classNames={{ label: 'font-bold' }}
                  errorMessage={errors.cepInicio?.message}
                  {...register('cepInicio')}
                  value={values.cepInicio}
                  onValueChange={(value) => {
                    setValue('cepInicio', cepMask(value));
                  }}
                />

                <Input
                  size="md"
                  type="text"
                  label="CEP Fim"
                  variant="underlined"
                  classNames={{ label: 'font-bold' }}
                  errorMessage={errors.cepFim?.message}
                  {...register('cepFim')}
                  value={values.cepFim}
                  onValueChange={(value) => {
                    setValue('cepFim', cepMask(value));
                  }}
                />
              </div>
            </ModalBody>

            <ModalFooter>
              <div className="flex w-full items-center justify-between gap-3">
                <div>
                  {parceiro && (
                    <Button
                      color="danger"
                      variant="flat"
                      startContent={<FaTrash />}
                      isLoading={isDeleting}
                      onPress={handleDeleteParceiro}
                    >
                      Excluir
                    </Button>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button color="danger" onPress={handleClose}>
                    Cancelar
                  </Button>

                  <Button
                    type="submit"
                    isLoading={isLoading}
                    isDisabled={!isValid || isDeleting}
                  >
                    <p>{parceiro ? 'Alterar' : 'Cadastrar'}</p>
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default forwardRef(RegisterParceiro);
